import {
  ReviewPayeeDirectoryRef,
  ReviewPayeeRole,
  ReviewPayeeSnapshot,
} from '@/modules/document/types'

export interface ReviewPayeeState extends ReviewPayeeSnapshot {
  directoryRef: ReviewPayeeDirectoryRef | null
  updateDirectory: boolean
  companyName: string
  companyNameAffix: string
  salutation: string
  title: string
  firstName: string
  lastName: string
  address: {
    street: string
    zipCode: string
    city: string
    countryIso: string
  }
}

export interface ReviewPayeeInput {
  version?: 1
  role?: ReviewPayeeRole
  directoryRef?: (Omit<ReviewPayeeDirectoryRef, 'id'> & { id: number | string }) | null
  updateDirectory?: boolean | null
  companyName?: string | null
  companyNameAffix?: string | null
  salutation?: string | null
  title?: string | null
  firstName?: string | null
  lastName?: string | null
  address?: {
    street?: string | null
    zipCode?: string | null
    city?: string | null
    countryIso?: string | null
  } | null
}

export type ReviewPayeeEdit = Partial<Pick<ReviewPayeeState,
  'companyName' | 'companyNameAffix' | 'salutation' | 'title' | 'firstName' | 'lastName'>> & {
  address?: Partial<ReviewPayeeState['address']>
}

export interface ReviewPayeeValidation {
  isValid: boolean
  errors: Partial<Record<'companyName' | 'lastName', 'required'>>
}

const valueOrEmpty = (value?: string | null) => value ?? ''

export const createReviewPayeeState = (input?: ReviewPayeeInput | null): ReviewPayeeState => ({
  version: 1,
  role: input?.role ?? 'supplier',
  directoryRef: input?.directoryRef
    ? { ...input.directoryRef, id: Number(input.directoryRef.id) }
    : null,
  updateDirectory: !!input?.directoryRef && input?.updateDirectory === true,
  companyName: valueOrEmpty(input?.companyName),
  companyNameAffix: valueOrEmpty(input?.companyNameAffix),
  salutation: valueOrEmpty(input?.salutation),
  title: valueOrEmpty(input?.title),
  firstName: valueOrEmpty(input?.firstName),
  lastName: valueOrEmpty(input?.lastName),
  address: {
    street: valueOrEmpty(input?.address?.street),
    zipCode: valueOrEmpty(input?.address?.zipCode),
    city: valueOrEmpty(input?.address?.city),
    countryIso: input?.address?.countryIso || 'DE',
  },
})

export const getReviewPayeeFromReceiptData = (
  receiptData?: { reviewPayee?: ReviewPayeeInput | null } | null,
) => createReviewPayeeState(receiptData?.reviewPayee)

const joinName = (...parts: Array<string | null | undefined>) => parts
  .map(part => part?.trim())
  .filter(Boolean)
  .join(' ')

export const getReviewPayeeDisplayName = (state: ReviewPayeeState) => (
  state.role === 'privateCustomer'
    ? joinName(state.title, state.firstName, state.lastName)
    : joinName(state.companyName, state.companyNameAffix)
)

export const validateReviewPayee = (state: ReviewPayeeState): ReviewPayeeValidation => {
  const errors: ReviewPayeeValidation['errors'] = {}

  if (state.role === 'privateCustomer') {
    if (!state.lastName.trim()) errors.lastName = 'required'
  } else if (!state.companyName.trim()) {
    errors.companyName = 'required'
  }

  return { isValid: Object.keys(errors).length === 0, errors }
}

export const changeReviewPayeeRole = (
  state: ReviewPayeeState,
  role: ReviewPayeeRole,
): ReviewPayeeState => role === state.role
  ? state
  : { ...state, role, directoryRef: null, updateDirectory: false }

const hasChanged = (state: ReviewPayeeState, edit: ReviewPayeeEdit) => {
  const identityChanged = Object.entries(edit)
    .filter(([key]) => key !== 'address')
    .some(([key, value]) => state[key as keyof ReviewPayeeEdit] !== value)
  const addressChanged = Object.entries(edit.address ?? {})
    .some(([key, value]) => state.address[key as keyof ReviewPayeeState['address']] !== value)

  return identityChanged || addressChanged
}

export const updateReviewPayee = (
  state: ReviewPayeeState,
  edit: ReviewPayeeEdit,
): ReviewPayeeState => {
  const changed = hasChanged(state, edit)
  return {
    ...state,
    ...edit,
    updateDirectory: state.updateDirectory || !!(state.directoryRef && changed),
    address: {
      ...state.address,
      ...edit.address,
    },
  }
}

const assertReferenceMatchesRole = (role: ReviewPayeeRole, directoryRef: ReviewPayeeDirectoryRef) => {
  if (role === 'supplier' && directoryRef.type !== 'supplier') {
    throw new Error('supplier review payees require a supplier directory reference')
  }
  if (role !== 'supplier' && directoryRef.type !== 'customer') {
    throw new Error('customer review payees require a customer directory reference')
  }
}

export const selectReviewPayee = (
  _state: ReviewPayeeState,
  selection: ReviewPayeeInput & { role: ReviewPayeeRole, directoryRef: ReviewPayeeDirectoryRef },
): ReviewPayeeState => {
  assertReferenceMatchesRole(selection.role, selection.directoryRef)
  return createReviewPayeeState(selection)
}

export const applyReviewPayeeToReceiptData = <T extends Record<string, unknown>>(
  receiptData: T,
  reviewPayee: ReviewPayeeState,
) => {
  const displayName = getReviewPayeeDisplayName(reviewPayee)
  const supplierId = reviewPayee.directoryRef?.type === 'supplier'
    ? reviewPayee.directoryRef.id
    : null
  const customerId = reviewPayee.directoryRef?.type === 'customer'
    ? reviewPayee.directoryRef.id
    : null

  return {
    ...receiptData,
    reviewPayee,
    payee: displayName,
    sender: displayName,
    type: reviewPayee.role === 'supplier' ? 'supplier' : 'customer',
    baseDataId: reviewPayee.directoryRef?.id ?? null,
    supplierId,
    customerId,
  }
}
